import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  BarChart3, TrendingUp, PhoneIncoming, CheckCircle2, 
  Filter, X, FileSpreadsheet, FileText,
  ChevronDown, ChevronUp, MessageSquare, Bot, Clock, Calendar, Phone, Mail, Building, UserCircle, User
} from 'lucide-react';
import { InteractionLifecycle, ContactType, InteractionStatus, InteractionMessage, SenderType } from '@/types/bi';
import biApi from '@/services/biApi';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { AtomicLoadingScreen } from '@/components/ui/atomic-loader';
import { toast } from '@/components/ui/use-toast';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';

interface InteractionManagementProps {
  clientId?: number;
}

interface FilterState {
  protocol: string;
  customerName: string;
  customerPhone: string;
  departments: string[];
  connections: string[];
  typeContact: string | 'all';
  agents: string[];
  slaStatus: string | 'all';
  startDateFrom: string;
  startDateTo: string;
  endDateFrom: string;
  endDateTo: string;
}

const InteractionManagement: React.FC<InteractionManagementProps> = ({ clientId }) => {
  const [interactions, setInteractions] = useState<InteractionLifecycle[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(true);
  const [departments, setDepartments] = useState<string[]>([]);
  const [connections, setConnections] = useState<string[]>([]);
  const [agents, setAgents] = useState<Array<{ agent_id: string; department: string }>>([]);
  const [selectedProtocol, setSelectedProtocol] = useState<string | null>(null);
  const [interactionDetails, setInteractionDetails] = useState<{ interaction: InteractionLifecycle; messages: InteractionMessage[] } | null>(null);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  
  const [filters, setFilters] = useState<FilterState>({
    protocol: '',
    customerName: '',
    customerPhone: '',
    departments: [],
    connections: [],
    typeContact: 'all',
    agents: [],
    slaStatus: 'all',
    startDateFrom: '',
    startDateTo: '',
    endDateFrom: '',
    endDateTo: '',
  });

  // Métricas calculadas
  const totalInteractions = interactions.length;
  const activeInteractions = interactions.filter(i => i.status === InteractionStatus.OPEN || i.status === InteractionStatus.PENDING).length;
  const receptiveInteractions = interactions.filter(i => i.type_contact === ContactType.RECEPTIVO).length;
  const withinSLA = interactions.filter(i => !i.sla_violation).length;
  const slaPercentage = totalInteractions > 0 ? ((withinSLA / totalInteractions) * 100).toFixed(1) : '0.0';

  useEffect(() => {
    if (clientId) {
      console.log('🔄 [InteractionManagement] ClientId mudou, carregando dados:', clientId);
      // Resetar filtros quando cliente muda
      const resetFilters: FilterState = {
        protocol: '',
        customerName: '',
        customerPhone: '',
        departments: [],
        connections: [],
        typeContact: 'all',
        agents: [],
        slaStatus: 'all',
        startDateFrom: '',
        startDateTo: '',
        endDateFrom: '',
        endDateTo: '',
      };
      setFilters(resetFilters);
      loadFilters();
      // Carregar interações com filtros resetados
      loadInteractionsWithFilters(resetFilters);
    } else {
      console.log('⚠️ [InteractionManagement] ClientId não definido');
      setInteractions([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  const loadFilters = async () => {
    if (!clientId) return;
    try {
      const [depts, conns, agts] = await Promise.all([
        biApi.getInteractionDepartments(clientId),
        biApi.getInteractionConnections(clientId),
        biApi.getInteractionAgents(clientId)
      ]);
      setDepartments(depts);
      setConnections(conns);
      setAgents(agts);
    } catch (error) {
      console.error('Erro ao carregar filtros:', error);
    }
  };

  const loadInteractionsWithFilters = async (filtersToUse: FilterState = filters) => {
    if (!clientId) {
      console.warn('⚠️ [InteractionManagement] loadInteractions chamado sem clientId');
      return;
    }
    
    setIsLoading(true);
    setInteractions([]); // Limpar dados anteriores
    
    try {
      console.log('🔍 [InteractionManagement] Carregando interações para clientId:', clientId, 'com filtros:', filtersToUse);
      
      const data = await biApi.getInteractions(
        clientId,
        filtersToUse.protocol || undefined,
        filtersToUse.customerName || undefined,
        filtersToUse.customerPhone || undefined,
        filtersToUse.departments.length > 0 ? filtersToUse.departments : undefined,
        filtersToUse.connections.length > 0 ? filtersToUse.connections : undefined,
        filtersToUse.typeContact !== 'all' ? filtersToUse.typeContact : undefined,
        filtersToUse.agents.length > 0 ? filtersToUse.agents : undefined,
        filtersToUse.slaStatus !== 'all' ? filtersToUse.slaStatus : undefined,
        filtersToUse.startDateFrom || undefined,
        filtersToUse.startDateTo || undefined,
        filtersToUse.endDateFrom || undefined,
        filtersToUse.endDateTo || undefined
      );
      
      console.log(`✅ [InteractionManagement] Carregadas ${data.length} interações`);
      console.log('📊 [InteractionManagement] Primeira interação (exemplo):', data[0] || 'Nenhuma interação');
      setInteractions(data || []);
    } catch (error: any) {
      console.error('❌ [InteractionManagement] Erro ao carregar interações:', error);
      console.error('❌ [InteractionManagement] Tipo do erro:', typeof error);
      console.error('❌ [InteractionManagement] Mensagem do erro:', error?.message);
      console.error('❌ [InteractionManagement] Stack do erro:', error?.stack);
      console.error('❌ [InteractionManagement] Erro completo:', JSON.stringify(error, null, 2));
      
      const errorMessage = error?.message || error?.error || 'Não foi possível carregar as interações';
      
      toast({
        title: 'Erro',
        description: errorMessage,
        variant: 'destructive',
      });
      setInteractions([]);
    } finally {
      setIsLoading(false);
    }
  };

  const loadInteractions = () => {
    loadInteractionsWithFilters();
  };

  const handleApplyFilters = () => {
    loadInteractions();
  };

  const handleClearFilters = () => {
    setFilters({
      protocol: '',
      customerName: '',
      customerPhone: '',
      departments: [],
      connections: [],
      typeContact: 'all',
      agents: [],
      slaStatus: 'all',
      startDateFrom: '',
      startDateTo: '',
      endDateFrom: '',
      endDateTo: '',
    });
  };

  const handleExportExcel = () => {
    // TODO: Implementar exportação Excel
    toast({
      title: 'Em desenvolvimento',
      description: 'Exportação Excel será implementada em breve',
    });
  };

  const handleExportPDF = () => {
    // TODO: Implementar exportação PDF
    toast({
      title: 'Em desenvolvimento',
      description: 'Exportação PDF será implementada em breve',
    });
  };

  const handleProtocolClick = async (protocol: string) => {
    if (!clientId) return;
    
    setSelectedProtocol(protocol);
    setIsLoadingDetails(true);
    
    try {
      const details = await biApi.getInteractionDetails(protocol, clientId);
      setInteractionDetails(details);
    } catch (error) {
      console.error('Erro ao carregar detalhes:', error);
      toast({
        title: 'Erro',
        description: 'Não foi possível carregar os detalhes da interação',
        variant: 'destructive',
      });
    } finally {
      setIsLoadingDetails(false);
    }
  };

  const handleCloseDetails = () => {
    setSelectedProtocol(null);
    setInteractionDetails(null);
  };

  const getStatusBadge = (status: InteractionStatus) => {
    const statusConfig = {
      [InteractionStatus.OPEN]: { label: 'Aberto', variant: 'default' as const },
      [InteractionStatus.PENDING]: { label: 'Pendente', variant: 'secondary' as const },
      [InteractionStatus.RESOLVED]: { label: 'Resolvido', variant: 'outline' as const },
      [InteractionStatus.CLOSED]: { label: 'Fechado', variant: 'secondary' as const },
      [InteractionStatus.CANCELLED]: { label: 'Cancelado', variant: 'destructive' as const },
    };
    
    const config = statusConfig[status] || { label: status, variant: 'outline' as const };
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  if (!clientId) {
    return (
      <Card className="border-0 shadow-lg">
        <CardContent className="p-12 text-center">
          <p className="text-gray-500 text-lg">Selecione um cliente para visualizar a gestão de atendimentos</p>
        </CardContent>
      </Card>
    );
  }

  if (isLoading && interactions.length === 0) {
    return <AtomicLoadingScreen />;
  }

  return (
    <div className="space-y-6">
      {/* Gestão de Atendimentos Section */}
      <Card className="border border-gray-200 shadow-sm">
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-xl font-semibold text-gray-900">Gestão de Atendimentos</CardTitle>
              <CardDescription className="text-sm text-gray-500 mt-1">Acompanhe e analise todos os atendimentos</CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Button 
                variant="outline" 
                onClick={() => setShowFilters(!showFilters)}
                className="gap-2 h-9 border-gray-300"
                size="sm"
              >
                {showFilters ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                {showFilters ? 'Ocultar' : 'Mostrar'} Filtros
              </Button>
              <Button 
                variant="outline" 
                onClick={handleExportExcel}
                className="gap-2 h-9 border-gray-300"
                size="sm"
              >
                <FileSpreadsheet className="h-4 w-4" />
                Excel
              </Button>
              <Button 
                variant="outline" 
                onClick={handleExportPDF}
                className="gap-2 h-9 border-gray-300"
                size="sm"
              >
                <FileText className="h-4 w-4" />
                PDF
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Métricas */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <Card className="border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
              <CardContent className="p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Total de Atendimentos</p>
                    <p className="text-2xl font-bold text-gray-900">{totalInteractions.toLocaleString('pt-BR')}</p>
                  </div>
                  <div className="p-2.5 rounded-lg bg-blue-50">
                    <BarChart3 className="h-5 w-5 text-blue-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
              <CardContent className="p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Atendimentos Ativos</p>
                    <p className="text-2xl font-bold text-gray-900">{activeInteractions.toLocaleString('pt-BR')}</p>
                  </div>
                  <div className="p-2.5 rounded-lg bg-green-50">
                    <TrendingUp className="h-5 w-5 text-green-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
              <CardContent className="p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Atendimentos Receptivos</p>
                    <p className="text-2xl font-bold text-gray-900">{receptiveInteractions.toLocaleString('pt-BR')}</p>
                  </div>
                  <div className="p-2.5 rounded-lg bg-purple-50">
                    <PhoneIncoming className="h-5 w-5 text-purple-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
              <CardContent className="p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Dentro do SLA</p>
                    <p className="text-2xl font-bold text-gray-900">{withinSLA.toLocaleString('pt-BR')}</p>
                    <p className="text-xs text-green-600 font-medium mt-1">{slaPercentage}%</p>
                  </div>
                  <div className="p-2.5 rounded-lg bg-green-50">
                    <CheckCircle2 className="h-5 w-5 text-green-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Filtros Avançados */}
          {showFilters && (
            <div className="border border-gray-200 rounded-lg p-6 mb-6 bg-white">
              <div className="flex items-center gap-2 mb-5">
                <Filter className="h-4 w-4 text-gray-500" />
                <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">Filtros Avançados</h3>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Coluna 1 */}
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium mb-2 block">Protocolo</label>
                    <Input
                      placeholder="Buscar por protocolo"
                      value={filters.protocol}
                      onChange={(e) => setFilters({ ...filters, protocol: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-2 block">Departamento</label>
                    <Select
                      value={filters.departments.length > 0 ? filters.departments[0] : 'all'}
                      onValueChange={(value) => setFilters({ ...filters, departments: value === 'all' ? [] : [value] })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Selecionar departamento" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todos</SelectItem>
                        {departments.map((dept) => (
                          <SelectItem key={dept} value={dept}>{dept}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-2 block">Data Início (De)</label>
                    <Input
                      type="date"
                      value={filters.startDateFrom}
                      onChange={(e) => setFilters({ ...filters, startDateFrom: e.target.value })}
                    />
                  </div>
                </div>

                {/* Coluna 2 */}
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium mb-2 block">Nome do Cliente</label>
                    <Input
                      placeholder="Buscar por nome"
                      value={filters.customerName}
                      onChange={(e) => setFilters({ ...filters, customerName: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-2 block">Conexão</label>
                    <Select
                      value={filters.connections.length > 0 ? filters.connections[0] : 'all'}
                      onValueChange={(value) => setFilters({ ...filters, connections: value === 'all' ? [] : [value] })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Selecionar conexão" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todas</SelectItem>
                        {connections.map((conn) => (
                          <SelectItem key={conn} value={conn}>{conn}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-2 block">Data Início (Até)</label>
                    <Input
                      type="date"
                      value={filters.startDateTo}
                      onChange={(e) => setFilters({ ...filters, startDateTo: e.target.value })}
                    />
                  </div>
                </div>

                {/* Coluna 3 */}
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium mb-2 block">Telefone</label>
                    <Input
                      placeholder="Buscar por telefone"
                      value={filters.customerPhone}
                      onChange={(e) => setFilters({ ...filters, customerPhone: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-2 block">Tipo</label>
                    <Select
                      value={filters.typeContact}
                      onValueChange={(value) => setFilters({ ...filters, typeContact: value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione o tipo" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todos</SelectItem>
                        <SelectItem value={ContactType.ATIVO}>Ativo</SelectItem>
                        <SelectItem value={ContactType.RECEPTIVO}>Receptivo</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-2 block">Data Fim (De)</label>
                    <Input
                      type="date"
                      value={filters.endDateFrom}
                      onChange={(e) => setFilters({ ...filters, endDateFrom: e.target.value })}
                    />
                  </div>
                </div>

                {/* Coluna 4 */}
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium mb-2 block">Atendente</label>
                    <Select
                      value={filters.agents.length > 0 ? filters.agents[0] : 'all'}
                      onValueChange={(value) => setFilters({ ...filters, agents: value === 'all' ? [] : [value] })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Selecionar atendente" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todos</SelectItem>
                        {agents.map((agent, index) => (
                          <SelectItem key={`${agent.agent_id}-${index}`} value={agent.agent_id}>
                            {agent.agent_id} ({agent.department})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-2 block">Status SLA</label>
                    <Select
                      value={filters.slaStatus}
                      onValueChange={(value) => setFilters({ ...filters, slaStatus: value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione o status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todos</SelectItem>
                        <SelectItem value="within">Dentro do SLA</SelectItem>
                        <SelectItem value="violated">Violado</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-2 block">Data Fim (Até)</label>
                    <Input
                      type="date"
                      value={filters.endDateTo}
                      onChange={(e) => setFilters({ ...filters, endDateTo: e.target.value })}
                    />
                  </div>
                </div>
              </div>

              {/* Botões de ação dos filtros */}
              <div className="flex items-center gap-2 mt-6 pt-4 border-t border-gray-200">
                <Button onClick={handleApplyFilters} className="gap-2 bg-gray-900 hover:bg-gray-800 text-white h-9">
                  <Filter className="h-4 w-4" />
                  Aplicar Filtros
                </Button>
                <Button variant="outline" onClick={handleClearFilters} className="gap-2 border-gray-300 h-9">
                  <X className="h-4 w-4" />
                  Limpar Filtros
                </Button>
              </div>
            </div>
          )}

          {/* Tabela de Interações */}
          <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Protocolo</TableHead>
                  <TableHead>Cliente</TableHead>
                  <TableHead>Telefone</TableHead>
                  <TableHead>Departamento</TableHead>
                  <TableHead>Atendente</TableHead>
                  <TableHead>Conexão</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>SLA</TableHead>
                  <TableHead>Data Início</TableHead>
                  <TableHead>Data Fim</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {interactions.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={11} className="text-center py-8 text-gray-500">
                      Nenhuma interação encontrada
                    </TableCell>
                  </TableRow>
                  ) : (
                    interactions.map((interaction) => (
                      <TableRow 
                        key={interaction.protocol} 
                        className="cursor-pointer hover:bg-gray-50/50 border-b border-gray-100 transition-colors"
                      >
                        <TableCell 
                          className="font-mono text-xs text-blue-600 hover:text-blue-700 hover:underline py-3"
                          onClick={() => handleProtocolClick(interaction.protocol)}
                        >
                          {interaction.protocol.substring(0, 8)}...
                        </TableCell>
                        <TableCell className="py-3 text-sm text-gray-900">{interaction.customer_name}</TableCell>
                        <TableCell className="py-3 text-sm text-gray-600">{interaction.customer_phone}</TableCell>
                        <TableCell className="py-3 text-sm text-gray-600">{interaction.final_department || interaction.initial_department || '-'}</TableCell>
                        <TableCell className="py-3 text-sm text-gray-600">{interaction.final_agent_id || interaction.initial_agent_id || '-'}</TableCell>
                        <TableCell className="py-3 text-sm text-gray-600">{interaction.connection || '-'}</TableCell>
                        <TableCell className="py-3">
                          {interaction.type_contact === ContactType.ATIVO ? (
                            <Badge variant="default" className="text-xs">Ativo</Badge>
                          ) : interaction.type_contact === ContactType.RECEPTIVO ? (
                            <Badge variant="secondary" className="text-xs">Receptivo</Badge>
                          ) : (
                            <span className="text-sm text-gray-400">-</span>
                          )}
                        </TableCell>
                        <TableCell className="py-3">{getStatusBadge(interaction.status)}</TableCell>
                        <TableCell className="py-3">
                          {interaction.sla_violation ? (
                            <Badge variant="destructive" className="text-xs">Violado</Badge>
                          ) : (
                            <Badge variant="outline" className="text-xs">OK</Badge>
                          )}
                        </TableCell>
                        <TableCell className="py-3 text-sm text-gray-600">
                          {interaction.start_time ? format(new Date(interaction.start_time), 'dd/MM/yyyy HH:mm', { locale: ptBR }) : '-'}
                        </TableCell>
                        <TableCell className="py-3 text-sm text-gray-600">
                          {interaction.end_time ? format(new Date(interaction.end_time), 'dd/MM/yyyy HH:mm', { locale: ptBR }) : '-'}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Modal de Detalhes */}
      <Dialog open={selectedProtocol !== null} onOpenChange={handleCloseDetails}>
        <DialogContent className="max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5" />
              Detalhes da Interação
            </DialogTitle>
            <DialogDescription>
              Protocolo: {selectedProtocol}
            </DialogDescription>
          </DialogHeader>

          {isLoadingDetails ? (
            <div className="flex items-center justify-center py-12">
              <AtomicLoadingScreen />
            </div>
          ) : interactionDetails ? (
            <ScrollArea className="max-h-[70vh] pr-4">
              <div className="space-y-6">
                {/* Informações da Interação */}
                <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
                  <div>
                    <h3 className="font-semibold text-sm text-gray-600 mb-3 flex items-center gap-2">
                      <UserCircle className="h-4 w-4" />
                      Informações do Cliente
                    </h3>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4 text-gray-400" />
                        <span className="font-medium">Nome:</span>
                        <span>{interactionDetails.interaction.customer_name}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Phone className="h-4 w-4 text-gray-400" />
                        <span className="font-medium">Telefone:</span>
                        <span>{interactionDetails.interaction.customer_phone}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Mail className="h-4 w-4 text-gray-400" />
                        <span className="font-medium">ID Cliente:</span>
                        <span className="font-mono text-xs">{interactionDetails.interaction.customer_id}</span>
                      </div>
                    </div>
                  </div>
                  <div>
                    <h3 className="font-semibold text-sm text-gray-600 mb-3 flex items-center gap-2">
                      <Building className="h-4 w-4" />
                      Informações do Atendimento
                    </h3>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">Departamento Inicial:</span>
                        <span>{interactionDetails.interaction.initial_department || '-'}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">Departamento Final:</span>
                        <span>{interactionDetails.interaction.final_department || '-'}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">Atendente Inicial:</span>
                        <span>{interactionDetails.interaction.initial_agent_id || '-'}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">Atendente Final:</span>
                        <span>{interactionDetails.interaction.final_agent_id || '-'}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">Conexão:</span>
                        <span>{interactionDetails.interaction.connection || '-'}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">Tipo:</span>
                        {interactionDetails.interaction.type_contact === ContactType.ATIVO ? (
                          <Badge variant="default">Ativo</Badge>
                        ) : interactionDetails.interaction.type_contact === ContactType.RECEPTIVO ? (
                          <Badge variant="secondary">Receptivo</Badge>
                        ) : (
                          <span>-</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Status e Métricas */}
                <div className="grid grid-cols-3 gap-4 p-4 bg-gray-50 rounded-lg">
                  <div>
                    <span className="text-sm text-gray-600">Status</span>
                    <div className="mt-1">{getStatusBadge(interactionDetails.interaction.status)}</div>
                  </div>
                  <div>
                    <span className="text-sm text-gray-600">SLA</span>
                    <div className="mt-1">
                      {interactionDetails.interaction.sla_violation ? (
                        <Badge variant="destructive">Violado</Badge>
                      ) : (
                        <Badge variant="outline">OK</Badge>
                      )}
                    </div>
                  </div>
                  <div>
                    <span className="text-sm text-gray-600">Transferido</span>
                    <div className="mt-1">
                      {interactionDetails.interaction.was_transferred ? (
                        <Badge variant="secondary">Sim</Badge>
                      ) : (
                        <Badge variant="outline">Não</Badge>
                      )}
                    </div>
                  </div>
                  {interactionDetails.interaction.sentiment_score !== null && (
                    <div>
                      <span className="text-sm text-gray-600">Sentimento</span>
                      <div className="mt-1">
                        <Badge variant={interactionDetails.interaction.sentiment_score >= 50 ? 'default' : 'destructive'}>
                          {interactionDetails.interaction.sentiment_score >= 50 ? '+' : ''}{interactionDetails.interaction.sentiment_score}
                        </Badge>
                      </div>
                    </div>
                  )}
                </div>

                {/* Datas */}
                <div className="grid grid-cols-3 gap-4 p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-gray-400" />
                    <div>
                      <span className="text-xs text-gray-600">Início</span>
                      <p className="text-sm font-medium">
                        {interactionDetails.interaction.start_time 
                          ? format(new Date(interactionDetails.interaction.start_time), 'dd/MM/yyyy HH:mm', { locale: ptBR })
                          : '-'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-gray-400" />
                    <div>
                      <span className="text-xs text-gray-600">Primeira Resposta</span>
                      <p className="text-sm font-medium">
                        {interactionDetails.interaction.first_response_time 
                          ? format(new Date(interactionDetails.interaction.first_response_time), 'dd/MM/yyyy HH:mm', { locale: ptBR })
                          : '-'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-gray-400" />
                    <div>
                      <span className="text-xs text-gray-600">Fim</span>
                      <p className="text-sm font-medium">
                        {interactionDetails.interaction.end_time 
                          ? format(new Date(interactionDetails.interaction.end_time), 'dd/MM/yyyy HH:mm', { locale: ptBR })
                          : '-'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Resumo da Conversa */}
                {interactionDetails.interaction.conversation_summary && (
                  <div className="p-4 bg-blue-50 rounded-lg">
                    <h3 className="font-semibold text-sm text-gray-700 mb-2">Resumo da Conversa</h3>
                    <p className="text-sm text-gray-600">{interactionDetails.interaction.conversation_summary}</p>
                  </div>
                )}

                {/* Tags */}
                {interactionDetails.interaction.tags && interactionDetails.interaction.tags.length > 0 && (
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <h3 className="font-semibold text-sm text-gray-700 mb-2">Tags</h3>
                    <div className="flex flex-wrap gap-2">
                      {interactionDetails.interaction.tags.map((tag, index) => (
                        <Badge key={index} variant="outline">{tag}</Badge>
                      ))}
                    </div>
                  </div>
                )}

                <Separator />

                {/* Mensagens */}
                <div>
                  <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
                    <MessageSquare className="h-5 w-5" />
                    Mensagens ({interactionDetails.messages.length})
                  </h3>
                  <div className="space-y-4">
                    {interactionDetails.messages.length === 0 ? (
                      <p className="text-sm text-gray-500 text-center py-8">Nenhuma mensagem encontrada</p>
                    ) : (
                      interactionDetails.messages.map((message) => (
                        <div
                          key={message.id}
                          className={`p-4 rounded-lg ${
                            message.sender_type === SenderType.AGENT
                              ? 'bg-blue-50 border-l-4 border-l-blue-500'
                              : 'bg-gray-50 border-l-4 border-l-gray-400'
                          }`}
                        >
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex items-center gap-2">
                              {message.sender_type === SenderType.AGENT ? (
                                <Bot className="h-4 w-4 text-blue-600" />
                              ) : (
                                <User className="h-4 w-4 text-gray-600" />
                              )}
                              <span className="font-semibold text-sm">
                                {message.sender_name}
                              </span>
                              <Badge variant={message.sender_type === SenderType.AGENT ? 'default' : 'secondary'}>
                                {message.sender_type === SenderType.AGENT ? 'Agente' : 'Cliente'}
                              </Badge>
                            </div>
                            <span className="text-xs text-gray-500">
                              {format(new Date(message.sent_at), 'dd/MM/yyyy HH:mm:ss', { locale: ptBR })}
                            </span>
                          </div>
                          <p className="text-sm text-gray-700 whitespace-pre-wrap">{message.content}</p>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            </ScrollArea>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InteractionManagement;

